import React from "react";
import { Checkbox, FormControlLabel } from "@mui/material";

const trimValue = (value) => String(value || "").trim();

export const toPartDateInput = (value = new Date()) => {
  if (!value) return "";
  if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return value;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

export const createPartInitialValues = (
  userId = "",
  defaultPartType = "Inventory",
) => ({
  name: "",
  partNumber: "",
  description: "",
  quantity: "",
  buyPrice: "",
  sellPrice: "",
  vendor: "",
  invoiceNo: "",
  invoiceDate: toPartDateInput(),
  partType: defaultPartType === "Non Inventory" ? "Non Inventory" : "Inventory",
  specialOrderStatus:
    defaultPartType === "Non Inventory" ? "Available" : "",
  refundAmount: "",
  statusNotes: "",
  user_id: userId,
});

export const preparePartFormValues = (
  value = {},
  userId = "",
  defaultPartType = "Inventory",
) => {
  const base = createPartInitialValues(userId, defaultPartType);

  return {
    ...base,
    ...value,
    name: trimValue(value.name),
    partNumber: trimValue(value.partNumber),
    description: value.description || "",
    quantity:
      value.quantity === 0 || value.quantity ? String(value.quantity) : "",
    buyPrice:
      value.buyPrice === 0 || value.buyPrice ? String(value.buyPrice) : "",
    sellPrice:
      value.sellPrice === 0 || value.sellPrice ? String(value.sellPrice) : "",
    vendor: trimValue(value.vendor),
    invoiceNo: trimValue(value.invoiceNo),
    invoiceDate: value.invoiceDate
      ? toPartDateInput(value.invoiceDate)
      : base.invoiceDate,
    partType:
      value.partType === "Non Inventory" ? "Non Inventory" : base.partType,
    specialOrderStatus:
      value.partType === "Non Inventory"
        ? value.specialOrderStatus || base.specialOrderStatus
        : "",
    refundAmount:
      value.refundAmount === 0 || value.refundAmount
        ? String(value.refundAmount)
        : "",
    statusNotes: value.statusNotes || "",
    user_id: value.user_id || userId,
  };
};

export const buildPartPayload = (
  value = {},
  userId = "",
  defaultPartType = "Inventory",
) => {
  const prepared = preparePartFormValues(value, userId, defaultPartType);

  return {
    ...prepared,
    name: trimValue(prepared.name),
    partNumber: trimValue(prepared.partNumber),
    description: prepared.description || "",
    quantity: Number(prepared.quantity || 0),
    buyPrice: Number(prepared.buyPrice || 0),
    sellPrice: Number(prepared.sellPrice || 0),
    vendor: trimValue(prepared.vendor),
    invoiceNo: trimValue(prepared.invoiceNo),
    invoiceDate: prepared.invoiceDate || "",
    partType:
      prepared.partType === "Non Inventory" ? "Non Inventory" : "Inventory",
    specialOrderStatus:
      prepared.partType === "Non Inventory"
        ? prepared.specialOrderStatus || "Available"
        : "",
    refundAmount:
      prepared.partType === "Non Inventory"
        ? Number(prepared.refundAmount || 0)
        : 0,
    statusNotes:
      prepared.partType === "Non Inventory" ? trimValue(prepared.statusNotes) : "",
    user_id: prepared.user_id || userId,
  };
};

export const getPartFormFields = (vendorOptions = [], currentValues = {}) => {
  const isSpecialOrder = (currentValues?.partType || "Inventory") === "Non Inventory";

  return [
    {
      name: "partType",
      label: "Part Type",
      type: "custom",
      component: ({ values, setFieldValue }) => (
        <FormControlLabel
          control={
            <Checkbox
              checked={(values.partType || "Inventory") === "Non Inventory"}
              onChange={(e) => {
                const nextType = e.target.checked ? "Non Inventory" : "Inventory";
                setFieldValue("partType", nextType);
                if (nextType === "Non Inventory") {
                  setFieldValue("specialOrderStatus", values.specialOrderStatus || "Available");
                } else {
                  setFieldValue("specialOrderStatus", "");
                  setFieldValue("refundAmount", "");
                  setFieldValue("statusNotes", "");
                }
              }}
            />
          }
          label="Special-order part (do not track stock)"
        />
      ),
    },
    { name: "name", label: "Part Name", type: "text" },
    { name: "partNumber", label: "Part Number", type: "text" },
    { name: "description", label: "Description", type: "text", multiline: true, rows: 2 },
    { name: "quantity", label: "Quantity", type: "number" },
    { name: "buyPrice", label: "Buy Price ($)", type: "number" },
    { name: "sellPrice", label: "Sell Price ($)", type: "number" },
    { name: "vendor", label: "Vendor", type: "select", options: vendorOptions },
    {
      name: "invoiceNo",
      label: isSpecialOrder ? "Vendor Invoice / Order No." : "Invoice No.",
      type: "text",
    },
    {
      name: "invoiceDate",
      label: isSpecialOrder ? "Purchase Date" : "Invoice Date",
      type: "date",
    },
    ...(isSpecialOrder
      ? [
          {
            name: "specialOrderStatus",
            label: "Status",
            type: "select",
            options: [
              { value: "Available", label: "Available" },
              { value: "Assigned", label: "Assigned" },
              { value: "Sold", label: "Sold" },
              { value: "Returned", label: "Returned" },
              { value: "Needs Review", label: "Needs Review" },
            ],
          },
          { name: "refundAmount", label: "Refund Amount ($)", type: "number" },
          {
            name: "statusNotes",
            label: "Status Notes",
            type: "text",
            multiline: true,
            rows: 2,
          },
        ]
      : []),
  ];
};
